import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { authApi, tasksApi } from '../api';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';
import {
  V3ConnectionsPanel,
  V3ModelSettingsPanel,
  V3RoutingSettingsPanel,
} from '../v3/pages';
import { v3Api, type V3Card, type V3Collection, type V3Home, type V3Resources } from '../v3/api';

type LoadState<T> = {
  loading: boolean;
  error: string;
  data: T | null;
};

type ProjectTab = 'overview' | 'tasks' | 'deliverables' | 'activity';
type SettingsTab = 'models' | 'connections' | 'diagnostics' | 'security';

type UserView = Record<string, unknown>;

function useLoad<T>(loader: () => Promise<T>, deps: unknown[] = []): LoadState<T> {
  const [state, setState] = useState<LoadState<T>>({ loading: true, error: '', data: null });
  useEffect(() => {
    let cancelled = false;
    setState((previous) => ({ ...previous, loading: true, error: '' }));
    loader()
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: '', data });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ loading: false, error: error instanceof Error ? error.message : String(error), data: null });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

function valueText(value: unknown, fallback = '暂无'): string {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function list(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object') : [];
}

function bool(value: unknown): boolean {
  return value === true;
}

function percent(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(100, Math.round(parsed))) : 0;
}

function shortText(value: unknown, fallback = '暂无说明', max = 120): string {
  const text = valueText(value, fallback).replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

const technicalSummaryPattern = /LLMRateLimitError|Traceback|stack trace|status\s*\d{3}|route_|executor_agent|execution_pipeline|namespace|redis|worker|uuid|sha-?256|raw error/i;

function humanSummary(value: unknown, fallback = '暂无说明', max = 120): string {
  const text = valueText(value, '').replace(/\s+/g, ' ').trim();
  if (!text || technicalSummaryPattern.test(text)) return fallback;
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function formatTime(value: unknown): string {
  const raw = valueText(value, '');
  if (!raw) return '暂无更新时间';
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? raw : date.toLocaleString('zh-CN', { hour12: false });
}

function userView(value: unknown): UserView {
  return record(value);
}

function userStatus(view: UserView): string {
  return valueText(view.user_visible_project_status, '状态暂时无法识别');
}

function actionType(view: UserView): string {
  const action = valueText(view.user_action_type, '');
  if (/project/i.test(action)) return '项目验收';
  if (/stage/i.test(action)) return '阶段验收';
  if (/task|review/i.test(action)) return '任务验收';
  if (/repair/i.test(action)) return '返修处理';
  return '人工确认';
}

function projectAction(view: UserView, links: Record<string, unknown>, projectId: string): { label: string; route: string } {
  if (bool(view.needs_user_action)) {
    const taskGate = /task|review/i.test(valueText(view.user_action_type, ''));
    return { label: `处理${actionType(view)}`, route: taskGate ? `/v3/acceptance/tasks/${projectId}` : valueText(links.acceptance, `/v3/acceptance/projects/${projectId}`) };
  }
  if (bool(view.artifact_preview_available) || bool(view.delivery_package_available)) {
    return { label: '查看交付物', route: `/v3/projects/${projectId}/deliverables` };
  }
  return { label: '查看项目进展', route: `/v3/projects/${projectId}/activity` };
}

function phaseFromView(view: UserView): string {
  const raw = valueText(view.project_status, '');
  if (raw === 'accepted_delivered') return '交付';
  if (raw === 'awaiting_project_acceptance' || raw === 'awaiting_review') return '验收';
  if (raw === 'project_repair_required') return '任务';
  if (raw === 'in_progress' || raw === 'failed') return '执行';
  if (valueText(view.current_stage_id, '')) return '任务';
  return '需求';
}

function pageTitle(eyebrow: string, title: string, description?: string) {
  return (
    <header className="r9-page-title">
      <span>{eyebrow}</span>
      <h1>{title}</h1>
      {description ? <p>{description}</p> : null}
    </header>
  );
}

function LoadingState() {
  return <div className="r9-state-panel">正在读取项目事实…</div>;
}

function ErrorState({ message }: { message: string }) {
  return <div className="r9-state-panel r9-state-error">读取失败：{message}</div>;
}

function EmptyState({ children }: { children: ReactNode }) {
  return <div className="r9-empty">{children}</div>;
}

function PrimaryAction({ to, children }: { to: string; children: ReactNode }) {
  return <Link className="r9-primary" to={to}>{children}</Link>;
}

function SecondaryAction({ to, children }: { to: string; children: ReactNode }) {
  return <Link className="r9-secondary" to={to}>{children}</Link>;
}

function StatusLabel({ status }: { status: string }) {
  return <span className="r9-status-label">{status}</span>;
}

function MetricCard({ label, value, to }: { label: string; value: unknown; to: string }) {
  return (
    <Link className="r9-metric" to={to}>
      <span>{label}</span>
      <strong>{valueText(value, '0')}</strong>
    </Link>
  );
}

function HomeItem({ card }: { card: V3Card }) {
  const route = card.next_action?.enabled ? card.next_action.route : card.project_id ? `/v3/projects/${card.project_id}` : '/v3/projects';
  const actionLabel = card.next_action?.enabled ? card.next_action.label : '查看详情';
  return (
    <article className="r9-item-row">
      <div className="r9-item-copy">
        <div className="r9-item-heading">
          <strong>{shortText(card.title, '未命名项目', 74)}</strong>
          <StatusLabel status={valueText(card.user_status, '状态暂时无法识别')} />
        </div>
        <p>{humanSummary(card.blocking_reason || card.summary, '需要处理，详细原因已记录。')}</p>
        {card.progress?.label ? <span className="r9-item-progress">{card.progress.label}</span> : null}
      </div>
      <SecondaryAction to={route}>{actionLabel}</SecondaryAction>
    </article>
  );
}

function inboxActionable(card: V3Card): boolean {
  const label = valueText(card.next_action?.label, '');
  const status = valueText(card.user_status, '');
  if (/查看详情|查看实时进度|查看项目执行|查看交付中心|查看交付物/.test(label)) return false;
  return /补充|确认|验收|返修|问题|失败|驳回|需要处理|人工/.test(`${label} ${status}`);
}

function HomeSection({ title, description, items, empty }: { title: string; description: string; items: V3Card[]; empty: string }) {
  return (
    <section className="r9-panel r9-list-panel">
      <div className="r9-panel-heading">
        <div><h2>{title}</h2><p>{description}</p></div>
        <span className="r9-panel-count">{items.length}</span>
      </div>
      {items.length ? <div className="r9-item-list">{items.map((item) => <HomeItem key={item.id} card={item} />)}</div> : <EmptyState>{empty}</EmptyState>}
    </section>
  );
}

export function R9HomePage() {
  const { loading, error, data } = useLoad<V3Home>(() => v3Api.home(), []);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState>暂时没有首页数据。</EmptyState>;

  const sections = data.sections || {};
  const overview = data.overview || {};
  const createAction = data.main_actions?.find((item) => item.label === '创建新需求') || { route: '/v3/requirements/new' };
  const handoffAction = data.main_actions?.find((item) => item.label === '接手已有项目');
  const importAction = data.main_actions?.find((item) => item.label === '导入已有大纲');
  return (
    <section className="r9-page">
      {pageTitle('杜工部工作台', '把项目推进到交付', '从需求开始，AI 负责执行，你只在需要判断的节点介入。')}
      <section className="r9-welcome-panel">
        <div>
          <span className="r9-kicker">开始一个新项目</span>
          <h2>把想法交给杜工部，先得到清晰的执行计划。</h2>
          <p>提交需求后，系统会依次完成方案、任务拆解、执行、验收和交付。</p>
        </div>
        <PrimaryAction to={valueText(createAction.route, '/v3/requirements/new')}>创建新需求</PrimaryAction>
      </section>

      <div className="r9-metric-grid">
        <MetricCard label="待我处理" value={overview['待我处理']} to="/v3/todo" />
        <MetricCard label="正在执行" value={overview['正在执行']} to="/v3/projects" />
        <MetricCard label="等待验收" value={overview['等待验收']} to="/v3/todo" />
        <MetricCard label="最近交付" value={overview['项目交付']} to="/v3/projects" />
      </div>

      <div className="r9-link-row">
        {handoffAction?.route ? <SecondaryAction to={handoffAction.route}>接手已有项目</SecondaryAction> : null}
        {importAction?.route ? <SecondaryAction to={importAction.route}>导入已有大纲</SecondaryAction> : null}
        <SecondaryAction to="/v3/projects">查看全部项目</SecondaryAction>
      </div>

      <div className="r9-home-grid">
        <HomeSection title="待我处理" description="需要你确认、补充或验收的事项。" items={sections['需要我处理'] || []} empty="当前没有需要你处理的事项。" />
        <HomeSection title="正在执行" description="AI 正在推进的项目和任务。" items={sections['正在运行'] || []} empty="当前没有正在执行的项目。" />
        <HomeSection title="等待验收" description="工作已经完成，等你做最后判断。" items={sections['等待验收'] || []} empty="当前没有等待验收的项目。" />
        <HomeSection title="最近项目" description="最近更新的正式项目。" items={sections['最近项目'] || []} empty="还没有正式项目。" />
        <HomeSection title="最近交付" description="具备真实交付证据的项目。" items={sections['候选交付'] || []} empty="当前没有可查看的交付物。" />
      </div>
    </section>
  );
}

export function R9TodoPage() {
  const { loading, error, data } = useLoad<V3Home>(() => v3Api.home(), []);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState>暂时没有待办数据。</EmptyState>;
  const sections = data.sections || {};
  const seen = new Set<string>();
  const items = [
    ...(sections['需要我处理'] || []),
    ...(sections['等待验收'] || []),
    ...(sections['异常提醒'] || []),
  ].filter((card) => {
    const key = `${card.entity_type}:${card.id}`;
    if (!card.next_action?.enabled || !card.next_action.route || !inboxActionable(card) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return (
    <section className="r9-page">
      {pageTitle('Human Inbox', '待我处理', '这里只显示需要你做决定、补充信息或验收的事项。')}
      <section className="r9-panel r9-todo-summary">
        <div><strong>{items.length}</strong><span>项需要关注</span></div>
        <p>AI 会继续处理不需要人工判断的工作；已完成、运行中和历史记录请到项目活动记录查看。</p>
      </section>
      <section className="r9-panel r9-list-panel">
        {items.length ? <div className="r9-item-list">{items.map((item) => <HomeItem key={`${item.id}-${item.entity_type}`} card={item} />)}</div> : <EmptyState>当前没有需要你处理的事项。</EmptyState>}
      </section>
    </section>
  );
}

export function R9ProjectsPage() {
  const { loading, error, data } = useLoad<V3Collection>(() => v3Api.projects(), []);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState>暂时没有项目数据。</EmptyState>;
  return (
    <section className="r9-page">
      {pageTitle('项目', '项目', '每个项目都有一条清晰的推进路径，从需求一直到交付。')}
      <div className="r9-page-actions"><PrimaryAction to="/v3/requirements/new">创建新需求</PrimaryAction></div>
      <section className="r9-project-list" aria-label="项目列表">
        {data.items.length ? data.items.map((card) => {
          const projectId = valueText(card.project_id, '');
          const route = projectId ? `/v3/projects/${projectId}` : card.next_action.route;
          return (
            <Link className="r9-project-row" to={route} key={card.id}>
              <div className="r9-project-row-main">
                <span className="r9-kicker">{valueText(card.tags?.[0], '正式项目')}</span>
                <h2>{shortText(card.title, '未命名项目', 100)}</h2>
                <p>{humanSummary(card.summary || card.blocking_reason, '项目正在准备中。')}</p>
              </div>
              <div className="r9-project-row-state"><StatusLabel status={valueText(card.user_status, '状态暂时无法识别')} /><span>{card.progress?.label || '进度待更新'}</span></div>
              <span className="r9-row-arrow" aria-hidden="true">→</span>
            </Link>
          );
        }) : <EmptyState>还没有正式项目。</EmptyState>}
      </section>
    </section>
  );
}

function Flow({ view }: { view: UserView }) {
  const current = phaseFromView(view);
  const phases = ['需求', '方案', '任务', '执行', '验收', '交付'];
  return (
    <section className="r9-flow-panel">
      <div className="r9-panel-heading"><div><h2>项目推进路径</h2><p>所有项目都沿着同一条路径推进。</p></div><span className="r9-flow-current">当前：{current}</span></div>
      <div className="r9-flow" aria-label="需求到交付的项目流程">
        {phases.map((phase, index) => <div className={`r9-flow-step${phase === current ? ' current' : ''}`} key={phase}><span>{index + 1}</span><strong>{phase}</strong></div>)}
      </div>
    </section>
  );
}

function TechnicalDetails({ view, data }: { view: UserView; data: Record<string, unknown> }) {
  const diagnostics = record(data.diagnostics);
  return (
    <details className="r9-technical">
      <summary>技术详情 / 审计记录</summary>
      <div className="r9-technical-grid">
        <div><span>Project ID</span><code>{valueText(view.project_id, '未记录')}</code></div>
        <div><span>Stage ID</span><code>{valueText(view.current_stage_id, '未记录')}</code></div>
        <div><span>Task ID</span><code>{valueText(view.current_task_id, '未记录')}</code></div>
        <div><span>Run ID</span><code>{valueText(view.current_run_id, '未记录')}</code></div>
        <div><span>Revision</span><code>{valueText(view.repair_revision, '未记录')}</code></div>
        <div><span>Formal version</span><code>{valueText(view.formal_version, '未记录')}</code></div>
        <div><span>Current run status</span><code>{valueText(view.current_run_status, '未记录')}</code></div>
        <div><span>Quality gate</span><code>{valueText(view.quality_gate_status, '未记录')}</code></div>
      </div>
      {Object.keys(diagnostics).length ? <pre>{JSON.stringify(diagnostics, null, 2)}</pre> : null}
    </details>
  );
}

function ProjectTabs({ projectId, active }: { projectId: string; active: ProjectTab }) {
  const tabs: Array<[ProjectTab, string]> = [['overview', '概览'], ['tasks', '任务'], ['deliverables', '交付物'], ['activity', '活动记录']];
  return <nav className="r9-project-tabs" aria-label="项目内容导航">{tabs.map(([key, label]) => <Link className={active === key ? 'active' : ''} to={`/v3/projects/${projectId}/${key}`} key={key}>{label}</Link>)}</nav>;
}

function ProjectHero({ data, view, projectId }: { data: Record<string, unknown>; view: UserView; projectId: string }) {
  const action = projectAction(view, record(data.links), projectId);
  const currentStage = record(data.current_stage);
  const currentTask = record(data.current_task);
  const needsAction = bool(view.needs_user_action);
  const healthy = !needsAction && !bool(view.repair_required);
  const currentPhase = phaseFromView(view);
  const currentStageLabel = valueText(currentStage.name, currentPhase === '交付' ? '交付已就绪' : valueText(view.current_stage_status, '项目流程已更新'));
  const aiDoingLabel = valueText(currentTask.title, currentPhase === '交付' ? '项目已完成，等待你查看交付物' : needsAction ? `等待你完成${actionType(view)}` : '正在推进下一步');
  return (
    <section className="r9-project-hero">
      <div className="r9-project-identity">
        <span className="r9-kicker">{valueText(data.product_type, '正式项目')}</span>
        <h2>{valueText(data.title, '未命名项目')}</h2>
        <p>{shortText(data.summary, '项目正在由杜工部推进。', 190)}</p>
      </div>
      <div className="r9-status-block">
        <span className="r9-kicker">当前项目状态</span>
        <strong>{userStatus(view)}</strong>
        <span className={healthy ? 'r9-health good' : 'r9-health attention'}>{healthy ? '当前运行正常' : needsAction ? '需要你处理' : '需要关注'}</span>
      </div>
      <div className="r9-project-answers">
        <div><span>当前做到</span><strong>{currentStageLabel}</strong></div>
        <div><span>AI 正在做什么</span><strong>{aiDoingLabel}</strong></div>
        <div><span>下一步</span><strong>{needsAction ? `请${actionType(view)}` : bool(view.artifact_preview_available) || bool(view.delivery_package_available) ? '查看交付物' : '继续观察进展'}</strong></div>
      </div>
      <div className="r9-hero-action"><PrimaryAction to={action.route}>{action.label}</PrimaryAction></div>
    </section>
  );
}

function OverviewTab({ data, view }: { data: Record<string, unknown>; view: UserView }) {
  const progress = record(data.progress);
  const overall = record(progress.overall);
  const stages = list(data.stages);
  return (
    <div className="r9-tab-content">
      <section className="r9-panel r9-progress-panel">
        <div className="r9-panel-heading"><div><h2>总体进度</h2><p>进度信息来自项目当前事实视图。</p></div><strong className="r9-big-number">{percent(overall.percent)}%</strong></div>
        <div className="r9-progress-track"><i style={{ width: `${percent(overall.percent)}%` }} /></div>
        <div className="r9-progress-meta"><span>{valueText(overall.label, '进度待更新')}</span><span>最近更新：{formatTime(view.last_updated_at || data.updated_at)}</span></div>
      </section>
      <section className="r9-panel">
        <div className="r9-panel-heading"><div><h2>阶段概览</h2><p>展开查看每个阶段包含的工作。</p></div></div>
        {stages.length ? <div className="r9-stage-list">{stages.map((stage, index) => <details className="r9-stage" key={valueText(stage.stage_id, String(index))} open={bool(stage.default_expanded)}><summary><span>{index + 1}</span><strong>{valueText(stage.name, `第 ${index + 1} 阶段`)}</strong><em>{valueText(record(stage.progress).label, `${list(stage.tasks).length} 项工作`)}</em></summary><div>{shortText(stage.summary, '该阶段的工作已纳入项目推进路径。')}</div></details>)}</div> : <EmptyState>阶段信息暂时不可用。</EmptyState>}
      </section>
    </div>
  );
}

function TasksTab({ data, view }: { data: Record<string, unknown>; view: UserView }) {
  const stages = list(data.stages);
  const currentTaskId = valueText(view.current_task_id, '');
  return (
    <div className="r9-tab-content">
      <section className="r9-panel">
        <div className="r9-panel-heading"><div><h2>项目任务</h2><p>查看每个阶段的工作与当前执行位置。</p></div><span className="r9-panel-count">{list(data.completed_tasks).length + list(data.failed_tasks).length || '—'}</span></div>
        {stages.length ? <div className="r9-task-groups">{stages.map((stage, index) => <section className="r9-task-group" key={valueText(stage.stage_id, String(index))}><header><h3>{valueText(stage.name, `第 ${index + 1} 阶段`)}</h3><span>{valueText(record(stage.progress).label, '阶段进度待更新')}</span></header>{list(stage.tasks).length ? list(stage.tasks).map((task, taskIndex) => { const taskId = valueText(task.task_id || task.task_package_id, `${index}-${taskIndex}`); const isCurrent = taskId === currentTaskId; return <div className={`r9-task-row${isCurrent ? ' current' : ''}`} key={taskId}><div><strong>{shortText(task.title, '未命名任务', 100)}</strong>{isCurrent ? <span className="r9-current-marker">当前</span> : null}</div><span>{isCurrent ? valueText(view.current_task_status, valueText(task.status, '状态待更新')) : valueText(task.status, '状态待更新')}</span></div>; }) : <EmptyState>本阶段暂无任务。</EmptyState>}</section>)}</div> : <EmptyState>任务信息暂时不可用。</EmptyState>}
      </section>
      <TechnicalDetails view={view} data={data} />
    </div>
  );
}

function DeliverablesTab({ data, view, projectId }: { data: Record<string, unknown>; view: UserView; projectId: string }) {
  const deliveryLoad = useLoad<Record<string, unknown>>(() => v3Api.deliveryDetail(projectId), [projectId]);
  const delivery = deliveryLoad.data || {};
  const previewEntry = valueText(delivery.preview_entry, '');
  const packageEntry = valueText(delivery.package_entry, '');
  const canPreview = bool(view.artifact_preview_available);
  const canPackage = bool(view.delivery_package_available);
  const formalVersion = valueText(view.formal_version, '未记录正式版本号');
  return (
    <div className="r9-tab-content">
      <section className="r9-panel r9-delivery-hero">
        <div><span className="r9-kicker">交付物</span><h2>从真实证据打开成品或下载交付包</h2><p>预览和下载分别受当前项目事实视图中的可用性控制。</p></div>
        <div className="r9-delivery-version"><span>正式版本</span><strong>{formalVersion}</strong></div>
      </section>
      {deliveryLoad.loading ? <LoadingState /> : deliveryLoad.error ? <ErrorState message={deliveryLoad.error} /> : null}
      <div className="r9-delivery-grid">
        <section className="r9-panel r9-artifact-card"><span className="r9-artifact-icon">◌</span><h2>成品预览</h2><p>{canPreview ? '已找到可验证的预览入口。' : '当前没有可验证的预览证据。'}</p>{canPreview && previewEntry ? <a className="r9-primary" href={previewEntry} target="_blank" rel="noreferrer">打开成品预览</a> : <button className="r9-disabled" disabled>暂无可用预览</button>}</section>
        <section className="r9-panel r9-artifact-card"><span className="r9-artifact-icon">↓</span><h2>交付包</h2><p>{canPackage ? '已找到可验证的正式交付包。' : '当前没有可验证的正式交付包。'}</p>{canPackage && packageEntry ? <a className="r9-primary" href={packageEntry} target="_blank" rel="noreferrer">下载交付包</a> : <button className="r9-disabled" disabled>暂无可用交付包</button>}</section>
      </div>
      {data.summary ? <section className="r9-panel"><h2>交付说明</h2><p>{shortText(data.summary, '暂无交付说明。', 220)}</p></section> : null}
      <TechnicalDetails view={view} data={data} />
    </div>
  );
}

function ActivityTab({ data, view }: { data: Record<string, unknown>; view: UserView }) {
  const history = record(data.history);
  const runs = list(history.runs);
  const failed = list(history.failed_tasks);
  return (
    <div className="r9-tab-content">
      <section className="r9-panel">
        <div className="r9-panel-heading"><div><h2>活动记录</h2><p>这里保留项目推进过程，技术细节默认收起。</p></div></div>
        <div className="r9-activity-list">
          <div className="r9-activity-row"><span className="r9-activity-dot good" /><div><strong>当前项目状态</strong><p>{userStatus(view)}</p></div><time>{formatTime(view.last_updated_at || data.updated_at)}</time></div>
          {runs.map((run, index) => <div className="r9-activity-row" key={valueText(run.run_id, String(index))}><span className="r9-activity-dot" /><div><strong>{valueText(run.title, '执行记录')}</strong><p>{valueText(run.user_status || run.status, '执行记录已更新')}</p></div><time>{formatTime(run.updated_at || run.created_at)}</time></div>)}
          {failed.map((task, index) => <div className="r9-activity-row" key={`failed-${valueText(task.task_id, String(index))}`}><span className="r9-activity-dot warning" /><div><strong>{valueText(task.title, '任务记录')}</strong><p>任务结果需要关注，详细原因已保留在审计记录中。</p></div><time>{formatTime(task.updated_at)}</time></div>)}
          {!runs.length && !failed.length ? <EmptyState>暂时没有更多活动记录。</EmptyState> : null}
        </div>
      </section>
      <TechnicalDetails view={view} data={data} />
    </div>
  );
}

export function R9ProjectCockpitPage({ tab: routeTab }: { tab?: ProjectTab }) {
  const { projectId = '' } = useParams();
  const tab = routeTab || 'overview';
  const { loading, error, data } = useLoad<Record<string, unknown>>(() => v3Api.projectDetail(projectId), [projectId]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState>没有找到这个项目。</EmptyState>;
  const view = userView(data.project_user_view);
  return (
    <section className="r9-page r9-project-page">
      {pageTitle('项目驾驶舱', valueText(data.title, '项目'), '项目的当前事实、推进路径和下一步都集中在这里。')}
      <ProjectHero data={data} view={view} projectId={projectId} />
      <Flow view={view} />
      <ProjectTabs projectId={projectId} active={tab} />
      {tab === 'overview' ? <OverviewTab data={data} view={view} /> : null}
      {tab === 'tasks' ? <TasksTab data={data} view={view} /> : null}
      {tab === 'deliverables' ? <DeliverablesTab data={data} view={view} projectId={projectId} /> : null}
      {tab === 'activity' ? <ActivityTab data={data} view={view} /> : null}
    </section>
  );
}

function diagnosticStatus(value: unknown): { label: string; tone: 'good' | 'warning' | 'danger' } {
  const text = valueText(value, '').toLowerCase();
  if (/ok|healthy|normal|ready|connected|reachable|pass|success/.test(text)) return { label: '正常', tone: 'good' };
  if (/config|unknown|未配置|unconfigured|not_configured|disabled/.test(text)) return { label: '未配置', tone: 'warning' };
  return { label: '异常', tone: 'danger' };
}

function diagnosticLabel(value: unknown): string {
  const text = valueText(value, '').toLowerCase();
  if (/api/.test(text)) return 'API';
  if (/worker|executor/.test(text)) return 'Worker';
  if (/queue|redis/.test(text)) return text.includes('redis') ? 'Redis' : '任务队列';
  if (/database|db|sql/.test(text)) return '数据库';
  if (/provider|gateway|model/.test(text)) return 'Provider Gateway';
  if (/runtime|execution/.test(text)) return '执行运行时';
  return valueText(value, '系统组件');
}

function diagnosticDetail(value: unknown): string {
  const text = valueText(value, '').replace(/\s+/g, ' ').trim();
  if (!text) return '状态已记录，暂无补充说明。';
  if (/timeout/i.test(text)) return '连接超时，请稍后重试或检查资源配置。';
  if (/auth|unauthor|permission|forbidden/i.test(text)) return '认证或权限未通过，请检查连接配置。';
  if (/error|fail|exception|traceback/i.test(text)) return '组件报告异常，详细信息已收进技术诊断。';
  return text.length > 140 ? `${text.slice(0, 139)}…` : text;
}

function SettingsTabs({ active }: { active: SettingsTab }) {
  const tabs: Array<[SettingsTab, string, string]> = [
    ['models', '模型与执行器', '/v3/settings/models'],
    ['connections', '资源与连接', '/v3/settings/connections'],
    ['diagnostics', '系统诊断', '/v3/settings/system'],
    ['security', '账号与安全', '/v3/settings/security'],
  ];
  return <nav className="r9-settings-tabs" aria-label="设置分类">{tabs.map(([key, label, to]) => <Link className={active === key ? 'active' : ''} to={to} key={key}>{label}</Link>)}</nav>;
}

function SettingsDiagnostics({ data }: { data: Record<string, unknown> }) {
  const checks = list(data.checks);
  return (
    <div className="r9-settings-content">
      <section className="r9-panel">
        <div className="r9-panel-heading"><div><h2>系统运行状况</h2><p>管理员视图只展示可行动的状态，原始诊断默认收起。</p></div></div>
        <div className="r9-diagnostic-list">
          {checks.length ? checks.map((check, index) => {
            const state = diagnosticStatus(check.status);
            return <article key={`${valueText(check.label, 'check')}-${index}`}><div><strong>{diagnosticLabel(check.label)}</strong><p>{diagnosticDetail(check.detail)}</p></div><span className={`r9-diagnostic-status ${state.tone}`}>{state.label}</span></article>;
          }) : <EmptyState>暂无系统诊断记录。</EmptyState>}
        </div>
      </section>
      <details className="r9-technical">
        <summary>技术诊断 / 审计记录</summary>
        <div className="r9-technical-grid">
          <div><span>环境</span><code>{valueText(data.environment, '未记录')}</code></div>
          <div><span>当前版本</span><code>{valueText(data.revision, '未记录')}</code></div>
          <div><span>镜像版本</span><code>{valueText(data.image_tag, '未记录')}</code></div>
        </div>
        <pre>{JSON.stringify({ checks: data.checks || [], links: data.links || {} }, null, 2)}</pre>
      </details>
    </div>
  );
}

function SettingsSecurity({ username, onChangePassword }: { username: string; onChangePassword: () => void }) {
  return (
    <div className="r9-settings-content">
      <section className="r9-panel r9-security-panel">
        <div className="r9-panel-heading"><div><h2>账号与安全</h2><p>管理当前登录账号和会话安全。</p></div></div>
        <dl className="r9-security-facts">
          <div><dt>当前账号</dt><dd>{username || '当前账号'}</dd></div>
          <div><dt>角色</dt><dd>管理员</dd></div>
          <div><dt>登录安全</dt><dd>当前会话已建立；退出登录仍在右上角。</dd></div>
        </dl>
        <button className="r9-secondary" type="button" onClick={onChangePassword}>修改密码</button>
      </section>
    </div>
  );
}

export function R9SettingsCenterPage({ tab: routeTab = 'models' }: { tab?: SettingsTab }) {
  const tab = routeTab;
  const resourceLoad = useLoad<V3Resources>(
    () => tab === 'connections' ? v3Api.resources() : Promise.resolve({ source: '', items: [], counts: {} }),
    [tab],
  );
  const systemLoad = useLoad<Record<string, unknown>>(
    () => tab === 'diagnostics' ? v3Api.system() as Promise<Record<string, unknown>> : Promise.resolve({}),
    [tab],
  );
  const [username, setUsername] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    if (tab !== 'security') return undefined;
    let cancelled = false;
    authApi.me().then((account) => { if (!cancelled) setUsername(account.username || ''); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [tab]);
  return (
    <section className="r9-page r9-settings-page">
      {pageTitle('工作区设置', '设置', '把模型、连接、系统诊断和账号安全放在一个清晰的地方。')}
      <SettingsTabs active={tab} />
      {tab === 'models' ? (
        <div className="r9-settings-content">
          <section className="r9-panel r9-settings-intro"><span className="r9-kicker">执行配置</span><h2>模型与执行器</h2><p>管理已配置的 Provider、默认模型和各工作角色的模型分工。密钥、完整地址和内部标识只在编辑或技术详情中出现。</p></section>
          <section className="r9-panel"><div className="r9-panel-heading"><div><h2>模型配置</h2><p>连接状态、默认模型和可用模型数量。</p></div></div><V3ModelSettingsPanel /></section>
          <section className="r9-panel"><div className="r9-panel-heading"><div><h2>模型分工</h2><p>为需求理解、规划、执行和验收选择已配置模型。</p></div></div><V3RoutingSettingsPanel /></section>
        </div>
      ) : null}
      {tab === 'connections' ? (
        resourceLoad.loading ? <LoadingState /> : resourceLoad.error ? <ErrorState message={resourceLoad.error} /> : <div className="r9-settings-content"><section className="r9-panel r9-settings-intro"><span className="r9-kicker">运行资源</span><h2>资源与连接</h2><p>VPS、GitHub 和其他执行资源单独管理，连接检查结果不会与模型配置混在一起。</p></section><section className="r9-panel"><V3ConnectionsPanel resources={resourceLoad.data} /></section></div>
      ) : null}
      {tab === 'diagnostics' ? (
        systemLoad.loading ? <LoadingState /> : systemLoad.error ? <ErrorState message={systemLoad.error} /> : <SettingsDiagnostics data={systemLoad.data || {}} />
      ) : null}
      {tab === 'security' ? <SettingsSecurity username={username} onChangePassword={() => setShowPassword(true)} /> : null}
      {showPassword ? <ChangePasswordDialog username={username} onClose={() => setShowPassword(false)} onSuccess={() => setShowPassword(false)} /> : null}
    </section>
  );
}

type GateResultLink = { href: string; enabled: boolean; reason: string };

function EvidenceLink({ label, result, className = 'r9-secondary' }: { label: string; result: GateResultLink; className?: string }) {
  return result.enabled ? <a className={className} href={result.href} target="_blank" rel="noreferrer">{label}</a> : <button className="r9-disabled" type="button" disabled title={result.reason}>{label}<small>{result.reason}</small></button>;
}

function criterionPassed(item: Record<string, unknown>): boolean {
  return /通过|已通过|accepted|completed|完成|succeeded|success/i.test(`${valueText(item.actual_result, '')} ${valueText(item.result_reason, '')}`);
}

function R9RepairTimeline({ view, data, projectId }: { view: UserView; data: Record<string, unknown>; projectId: string }) {
  if (!bool(view.repair_required) && !valueText(view.repair_revision, '')) return null;
  const reason = valueText(view.repair_reason, '修改意见已记录在当前返修历史中。');
  const currentStatus = userStatus(view);
  const revision = valueText(view.repair_revision, '当前');
  return (
    <section className="r9-panel r9-repair-panel">
      <div className="r9-panel-heading"><div><span className="r9-kicker">返修进度</span><h2>这次修改进行到哪里了</h2><p>当前状态来自项目事实视图；历史版本只在下方展开。</p></div><strong className="r9-repair-version">R{revision.replace(/^R/i, '')}</strong></div>
      <div className="r9-repair-timeline">
        <div className="done"><span>1</span><div><strong>第一次结果完成</strong><p>原始任务结果已保留，可在历史记录中追溯。</p></div></div>
        <div className="done"><span>2</span><div><strong>你提出修改</strong><p>{reason}</p></div></div>
        <div className="current"><span>3</span><div><strong>AI 已收到修改</strong><p>当前返修版本：R{revision.replace(/^R/i, '')}</p></div></div>
        <div className={bool(view.repair_required) ? 'current' : 'done'}><span>4</span><div><strong>{bool(view.repair_required) ? 'AI 正在返修' : '返修已完成'}</strong><p>{currentStatus}</p></div></div>
        <div className={!bool(view.repair_required) ? 'current' : ''}><span>5</span><div><strong>重新验收</strong><p>{bool(view.needs_user_action) ? '返修结果已回到人工验收。' : '返修完成后会回到人工验收。'}</p></div></div>
      </div>
      <div className="r9-entry-row">
        <Link className="r9-secondary" to={`/v3/projects/${projectId}/repairs`}>查看返修结果</Link>
        {bool(view.needs_user_action) ? <Link className="r9-primary" to={`/v3/acceptance/tasks/${projectId}`}>重新验收</Link> : <button className="r9-disabled" type="button" disabled>重新验收<small>返修完成后可用</small></button>}
      </div>
      {list(data.recent_runs).length ? <details className="r9-technical"><summary>查看历史版本与技术审计</summary>{list(data.recent_runs).map((run, index) => <div className="r9-history-row" key={`${valueText(run.run_id, 'run')}-${index}`}><strong>{valueText(run.user_status || run.status, '历史执行记录')}</strong><span>{formatTime(run.updated_at || run.created_at)}</span><code>{valueText(run.run_id, '未记录')}</code></div>)}</details> : null}
    </section>
  );
}

export function R9HumanGatePage() {
  const { projectId = '' } = useParams();
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairReason, setRepairReason] = useState('');
  const deliveryLoad = useLoad<Record<string, unknown>>(() => v3Api.deliveryDetail(projectId), [projectId]);
  const { loading, error, data } = useLoad<Record<string, unknown>>(() => v3Api.acceptanceDetail('tasks', projectId), [projectId, refreshKey]);
  if (!projectId) return <EmptyState>没有找到验收项目。</EmptyState>;
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState>没有找到验收详情。</EmptyState>;
  const view = record(data.project_user_view);
  const criteria = list(data.criteria);
  const evidence = list(data.evidence);
  const taskId = valueText(view.current_task_id, '');
  const needsDecision = bool(view.needs_user_action);
  const previewEvidence = evidence.find((item) => /成品预览|preview/i.test(valueText(item.type, '')) && valueText(item.route, ''));
  const testEvidence = evidence.find((item) => /测试结果|test/i.test(valueText(item.type, '')) && valueText(item.route, ''));
  const deliveryEvidence = evidence.find((item) => /产物|交付|artifact|package/i.test(valueText(item.type, '')) && valueText(item.route, ''));
  const delivery = deliveryLoad.data || {};
  const previewHref = valueText(previewEvidence?.route, '') || valueText(delivery.preview_entry, '');
  const packageHref = valueText(deliveryEvidence?.route, '') || valueText(delivery.package_entry, '');
  const resultLinks: { preview: GateResultLink; tests: GateResultLink; delivery: GateResultLink } = {
    preview: { href: previewHref, enabled: bool(view.artifact_preview_available) && !!previewHref, reason: '暂无可验证的成品预览入口' },
    tests: { href: valueText(testEvidence?.route, ''), enabled: !!valueText(testEvidence?.route, ''), reason: '暂无可验证的测试结果入口' },
    delivery: { href: packageHref, enabled: bool(view.delivery_package_available) && !!packageHref, reason: '暂无可验证的交付文件入口' },
  };
  const completedItems = criteria.length ? criteria.slice(0, 8) : evidence.slice(0, 8);
  const runDecision = async (decision: 'approve' | 'reject' | 'require_fix', reason: string) => {
    if (!taskId || busy) return;
    if (decision === 'require_fix' && !reason.trim()) { setRepairOpen(true); setMessage('请先写清楚希望修改的内容。'); return; }
    setBusy(decision);
    setMessage('');
    try {
      await tasksApi.reviewDecision(taskId, { decision, reason: reason.trim(), reviewed_by: 'r9_human_gate' }, projectId);
      setMessage(decision === 'approve' ? '已提交验收通过，正在刷新当前状态。' : decision === 'reject' ? '已记录驳回决定，正在刷新当前状态。' : '修改意见已提交，AI 正在准备返修。');
      setRepairReason('');
      setRepairOpen(false);
      setRefreshKey((value) => value + 1);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : '操作未完成，请稍后重试。');
    } finally {
      setBusy('');
    }
  };
  return (
    <section className="r9-page r9-gate-page">
      {pageTitle(needsDecision ? 'Human Gate' : '验收记录', needsDecision ? '任务已完成，等待你验收' : '当前任务验收记录', valueText(data.summary, needsDecision ? '请检查 AI 交付的结果，并决定是否通过。' : '当前任务没有待处理的人工动作。'))}
      <section className="r9-panel r9-gate-hero"><span className="r9-kicker">本次交付</span><h2>{valueText(data.title, '当前任务')}</h2><p>请按验收清单检查结果。只有你明确通过后，后续流程才会继续。</p><div className="r9-gate-facts"><div><span>当前状态</span><strong>{userStatus(view)}</strong></div><div><span>下一步</span><strong>{needsDecision ? '请你验收或提出修改' : '当前没有待处理的验收动作'}</strong></div></div></section>
      <R9RepairTimeline view={view} data={data} projectId={projectId} />
      <section className="r9-panel"><div className="r9-panel-heading"><div><h2>AI 这次完成了什么</h2><p>以下内容来自验收标准和已记录证据。</p></div></div>{completedItems.length ? <ul className="r9-completed-list">{completedItems.map((item, index) => <li key={`${valueText(item.criterion || item.title, 'item')}-${index}`}><span>✓</span><div><strong>{shortText(item.criterion || item.title, `验收项 ${index + 1}`, 150)}</strong>{item.actual_result ? <small>{shortText(item.actual_result, '结果已记录', 120)}</small> : null}</div></li>)}</ul> : <EmptyState>暂无可展示的验收条目。</EmptyState>}</section>
      <section className="r9-panel"><div className="r9-panel-heading"><div><h2>交付结果</h2><p>只有真实证据存在时，入口才会启用。</p></div></div><div className="r9-result-actions"><div><EvidenceLink label="打开成品" result={resultLinks.preview} className="r9-primary" /><span>{resultLinks.preview.enabled ? '已有可验证预览' : resultLinks.preview.reason}</span></div><div><EvidenceLink label="查看测试结果" result={resultLinks.tests} /><span>{resultLinks.tests.enabled ? '已有测试证据' : resultLinks.tests.reason}</span></div><div><EvidenceLink label="查看交付文件" result={resultLinks.delivery} /><span>{resultLinks.delivery.enabled ? '已有正式交付文件' : resultLinks.delivery.reason}</span></div></div></section>
      <section className="r9-panel"><div className="r9-panel-heading"><div><h2>验收清单</h2><p>逐项查看实际结果，再做决定。</p></div></div>{criteria.length ? <div className="r9-criteria-list">{criteria.map((item, index) => <label key={`${valueText(item.criterion, 'criterion')}-${index}`}><input type="checkbox" checked={criterionPassed(item)} readOnly /><span><strong>{shortText(item.criterion, `验收项 ${index + 1}`, 150)}</strong><small>{shortText(item.actual_result, '结果待确认', 120)}</small></span></label>)}</div> : <EmptyState>暂无验收标准。</EmptyState>}</section>
      {message ? <div className="r9-action-message" role="status" aria-live="polite">{message}</div> : null}
      <section className="r9-gate-actions"><button className="r9-primary" type="button" disabled={!needsDecision || !taskId || !!busy} onClick={() => void runDecision('approve', '用户查看交付后通过任务验收')}>{busy === 'approve' ? '正在提交…' : '通过验收'}</button><button className="r9-secondary" type="button" disabled={!needsDecision || !taskId || !!busy} onClick={() => setRepairOpen(true)}>需要修改</button><details className="r9-more-actions"><summary>更多</summary><button className="r9-danger" type="button" disabled={!needsDecision || !taskId || !!busy} onClick={() => void runDecision('reject', '用户驳回任务')}>驳回任务</button></details></section>
      {!needsDecision || !taskId ? <p className="r9-disabled-note">当前页面没有可执行的人工验收动作，按钮已按后端当前事实禁用。</p> : null}
      {repairOpen ? <div className="r9-modal-backdrop" role="presentation"><section className="r9-modal-panel" role="dialog" aria-modal="true" aria-labelledby="r9-repair-title"><div className="r9-modal-head"><div><span className="r9-kicker">返修请求</span><h2 id="r9-repair-title">告诉 AI 需要修改什么</h2></div><button className="r9-secondary" type="button" disabled={!!busy} onClick={() => setRepairOpen(false)}>关闭</button></div><p>请写具体、可检查的修改意见。提交后会保留在返修历史中。</p><textarea className="r9-repair-input" value={repairReason} onChange={(event) => setRepairReason(event.target.value)} placeholder="例如：采购成本输入需要放在运费之前，并保留错误提示。" rows={6} autoFocus /><div className="r9-modal-actions"><button className="r9-secondary" type="button" disabled={!!busy} onClick={() => setRepairOpen(false)}>取消</button><button className="r9-primary" type="button" disabled={!repairReason.trim() || !!busy} onClick={() => void runDecision('require_fix', repairReason)}>{busy === 'require_fix' ? '正在提交…' : '提交修改意见'}</button></div></section></div> : null}
    </section>
  );
}

function repairStatusText(value: unknown): string {
  const text = valueText(value, '').toLowerCase();
  if (/running|processing|in_progress/.test(text)) return 'AI 正在返修';
  if (/succeed|complete|done|accepted/.test(text)) return '返修结果已完成';
  if (/fail|error|reject/.test(text)) return '返修需要处理';
  if (/pending|queue|waiting/.test(text)) return '等待返修执行';
  return valueText(value, '状态待更新');
}

export function R9RepairPage() {
  const { projectId = '' } = useParams();
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [summaryInput, setSummaryInput] = useState('');
  const [testsInput, setTestsInput] = useState('');
  const [previewInput, setPreviewInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const { loading, error, data } = useLoad<Record<string, unknown>>(() => v3Api.projectRepairs(projectId), [projectId, refreshKey]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState>没有找到返修数据。</EmptyState>;
  const view = record(data.project_user_view);
  const tasks = list(data.tasks);
  const selected = record(tasks.find((item) => valueText(item.task_id, '') === selectedTaskId) || tasks[0]);
  const taskId = valueText(selected.task_id, '');
  const repairReason = valueText(selected.repair_reason || view.repair_reason, '原始修改意见已记录，但当前页面没有更多说明。');
  const actions = list(selected.allowed_actions);
  const canSubmitResult = actions.some((item) => valueText(item.enabled) === 'true' && /result|repair|submit/i.test(valueText(item.key || item.action || item.label, '')));
  const terminal = valueText(view.project_status, '').toLowerCase() === 'accepted_delivered';
  const submitResult = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!taskId || !summaryInput.trim() || busy || !canSubmitResult) return;
    setBusy(true);
    setMessage('');
    try {
      await v3Api.submitRepairResult(projectId, taskId, { summary: summaryInput.trim(), build_and_test_result: testsInput.trim(), preview: previewInput.trim(), submitter: 'user' });
      setSummaryInput(''); setTestsInput(''); setPreviewInput('');
      setMessage('返修结果已提交，正在刷新当前事实。');
      setRefreshKey((value) => value + 1);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : '返修结果提交失败，请稍后重试。');
    } finally {
      setBusy(false);
    }
  };
  const result = record(selected.repair_result);
  const history = [...list(selected.review_history), ...list(selected.repair_result_history)];
  return (
    <section className="r9-page r9-repair-page">
      {pageTitle('返修中心', terminal ? '返修历史' : '返修进度', terminal ? '项目已完成，历史返修只用于追溯。' : '把修改意见、当前版本和重新验收放在同一条时间线上。')}
      <section className="r9-panel r9-repair-summary"><div><span className="r9-kicker">当前项目状态</span><h2>{userStatus(view)}</h2><p>{valueText(data.rc1_failure_reason, '返修来自人工验收意见。')}</p></div><div><span className="r9-kicker">当前返修版本</span><strong className="r9-repair-version">R{valueText(view.repair_revision, '—').replace(/^R/i, '')}</strong></div></section>
      {tasks.length > 1 ? <section className="r9-panel"><div className="r9-panel-heading"><div><h2>返修事项</h2><p>选择一个返修事项查看结果。</p></div></div><div className="r9-repair-task-list">{tasks.map((item, index) => { const id = valueText(item.task_id, String(index)); return <button type="button" className={id === taskId ? 'active' : ''} key={id} onClick={() => setSelectedTaskId(id)}><strong>{shortText(item.title, '返修事项', 90)}</strong><span>{repairStatusText(item.execution_status)}</span></button>; })}</div></section> : null}
      {taskId ? <section className="r9-panel r9-repair-detail"><div className="r9-panel-heading"><div><span className="r9-kicker">返修事项</span><h2>{shortText(selected.title, '当前返修事项', 120)}</h2><p>原始结果与修改意见均来自当前返修记录。</p></div><span className="r9-status-label">{repairStatusText(selected.execution_status || view.current_task_status)}</span></div><div className="r9-repair-facts"><div><span>原始结果</span><strong>{shortText(selected.current_actual_result || selected.rc1_issue_source, '原始验收结果已保留。', 180)}</strong></div><div><span>你的修改意见</span><strong>{repairReason}</strong></div><div><span>返修结果</span><strong>{shortText(selected.fix_result_preview || result.summary, '返修结果尚未记录。', 180)}</strong></div></div><div className="r9-repair-timeline compact"><div className="done"><span>1</span><div><strong>第一次结果完成</strong><p>原始结果保留在当前项目历史中。</p></div></div><div className="done"><span>2</span><div><strong>你提出修改</strong><p>{repairReason}</p></div></div><div className="current"><span>3</span><div><strong>{repairStatusText(selected.execution_status)}</strong><p>{shortText(selected.fix_result_status || selected.rc2_impact, '当前返修状态来自返修记录。', 150)}</p></div></div><div className={bool(view.needs_user_action) ? 'current' : ''}><span>4</span><div><strong>重新验收</strong><p>{bool(view.needs_user_action) ? '返修结果已等待你重新验收。' : '返修完成后回到人工验收。'}</p></div></div></div><div className="r9-entry-row"><Link className="r9-secondary" to={`/v3/acceptance/tasks/${projectId}`}>查看返修结果</Link>{bool(view.needs_user_action) ? <Link className="r9-primary" to={`/v3/acceptance/tasks/${projectId}`}>重新验收</Link> : null}</div></section> : <EmptyState>当前没有可展示的返修事项。</EmptyState>}
      {taskId && !terminal && canSubmitResult ? <section className="r9-panel"><div className="r9-panel-heading"><div><h2>补充返修结果</h2><p>提交结果不会自动通过验收，仍需人工确认。</p></div></div><form className="r9-repair-form" onSubmit={(event) => void submitResult(event)}><label>修复结果摘要<textarea value={summaryInput} onChange={(event) => setSummaryInput(event.target.value)} placeholder="说明这次实际修改了什么。" rows={4} required /></label><label>构建与测试结果<textarea value={testsInput} onChange={(event) => setTestsInput(event.target.value)} placeholder="填写真实的构建或测试结论。" rows={3} /></label><label>返修结果预览<input value={previewInput} onChange={(event) => setPreviewInput(event.target.value)} placeholder="真实预览入口或说明，可为空。" /></label><button className="r9-primary" type="submit" disabled={busy || !summaryInput.trim()}>{busy ? '正在提交…' : '提交返修结果'}</button></form></section> : null}
      {message ? <div className="r9-action-message" role="status" aria-live="polite">{message}</div> : null}
      <details className="r9-technical"><summary>历史版本 / 技术审计</summary><div className="r9-technical-grid"><div><span>返修 revision</span><code>{valueText(view.repair_revision, '未记录')}</code></div><div><span>Task ID</span><code>{taskId || '未记录'}</code></div><div><span>Parent Run</span><code>{valueText(selected.parent_run_id, '未记录')}</code></div></div>{history.length ? history.map((item, index) => <div className="r9-history-row" key={`${valueText(item.reviewed_at || item.submitted_at, 'history')}-${index}`}><strong>{valueText(item.result || item.status, '历史记录')}</strong><span>{formatTime(item.reviewed_at || item.submitted_at)}</span><p>{shortText(item.opinion || item.reason || item.summary, '历史记录已保留。', 180)}</p></div>) : <EmptyState>暂无历史记录。</EmptyState>}</details>
    </section>
  );
}

export function R9DeliveryRedirect() {
  const { projectId = '' } = useParams();
  return <Navigate replace to={`/v3/projects/${projectId}/deliverables`} />;
}
